import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

interface WorkApplication {
  id: string;
  contractingUnit: string;
  workLocation: string;
  workPeriodStart: string;
  workPeriodEnd: string;
  status: string; // pending, approved, rejected, completed
}

interface WorkDay {
  id?: string;
  date: string;
  isWorkingDay: boolean;
  workType1: boolean; // 動火作業
  workType2: boolean; // 吊掛作業
  workType3: boolean; // 局限空間作業
  workType4: boolean; // 有機溶劑作業
  workType5: boolean; // 高架作業
  workType6: boolean; // 消防系統及火警系統隔離
  workDescription: string;
  personnel: Personnel[];
}

interface Personnel {
  id: string;
  name: string;
  idNumber: string;
  phone: string;
  jobType: string;
  qualifications?: string[]; // 人員擁有的證照
}

@Component({
  selector: 'app-work-overview',
  templateUrl: './work-overview.component.html',
  styleUrls: ['./work-overview.component.scss'],
  standalone: true,

  imports: [CommonModule, FormsModule, ReactiveFormsModule], // 這裡可以引入需要的模組或元件
})
export class WorkOverviewComponent implements OnInit {
  // 施工申請單
  workApplications: WorkApplication[] = [];
  filteredWorkApplications: WorkApplication[] = [];
  selectedWorkApplication: WorkApplication | null = null;
  applicationSearch: string = '';

  // 施工日程
  workDaysList: WorkDay[] = [];
  availableDates: string[] = [];
  editingWorkDay: WorkDay | null = null;

  // 表單
  workDayForm: FormGroup;

  // 模態框狀態
  showWorkDayModal: boolean = false;
  showPersonnelModal: boolean = false;

  // 人員資料
  contractorPersonnel: Personnel[] = [];
  filteredContractorPersonnel: Personnel[] = [];
  selectedPersonnel: Personnel[] = [];
  personnel: Personnel[] = [];
  personnelSearch: string = '';

  constructor(private fb: FormBuilder) {
    this.workDayForm = this.fb.group({
      workDate: ['', Validators.required],
      isWorkingDay: [true],
      workType1: [false],
      workType2: [false],
      workType3: [false],
      workType4: [false],
      workType5: [false],
      workType6: [false],
      workDescription: [''],
    });
  }

  ngOnInit(): void {
    // 載入模擬資料
    this.loadMockData();
    this.filterApplications();
  }

  // 載入模擬資料
  loadMockData(): void {
    // 模擬施工申請單數據
    this.workApplications = [
      {
        id: 'WORK-2023-001',
        contractingUnit: '宏達電力工程有限公司',
        workLocation: 'A區機房',
        workPeriodStart: '2025-06-01',
        workPeriodEnd: '2025-06-30',
        status: 'approved',
      },
      {
        id: 'WORK-2023-002',
        contractingUnit: '冠好冷氣空調工程',
        workLocation: 'B區辦公室',
        workPeriodStart: '2025-06-10',
        workPeriodEnd: '2025-06-30',
        status: 'approved',
      },
      {
        id: 'WORK-2023-003',
        contractingUnit: '永信水電工程有限公司',
        workLocation: 'C區餐廳',
        workPeriodStart: '2025-06-05',
        workPeriodEnd: '2025-06-30',
        status: 'approved',
      },
    ];

    // 模擬承包商人員資料
    this.contractorPersonnel = [
      {
        id: 'P001',
        name: '張志明',
        idNumber: 'A123456789',
        phone: '0912-345-678',
        jobType: '電工',
        qualifications: ['動火作業證照', '高架作業證照']
      },
      {
        id: 'P002',
        name: '林小華',
        idNumber: 'B234567890',
        phone: '0923-456-789',
        jobType: '水電工',
        qualifications: ['局限空間作業證照', '有機溶劑作業證照']
      },
      {
        id: 'P003',
        name: '王大明',
        idNumber: 'C345678901',
        phone: '0934-567-890',
        jobType: '空調技師',
        qualifications: ['消防系統作業證照']
      },
      {
        id: 'P004',
        name: '李正雄',
        idNumber: 'D456789012',
        phone: '0945-678-901',
        jobType: '電工',
        qualifications: ['高架作業證照']
      },
      {
        id: 'P005',
        name: '陳美玲',
        idNumber: 'E567890123',
        phone: '0956-789-012',
        jobType: '機電工程師',
        qualifications: ['吊掛作業證照', '消防系統作業證照']
      }
    ];

    this.filteredContractorPersonnel = [...this.contractorPersonnel];
  }

  // 搜尋過濾施工申請單
  filterApplications(): void {
    if (!this.applicationSearch) {
      this.filteredWorkApplications = [...this.workApplications];
    } else {
      const search = this.applicationSearch.toLowerCase();
      this.filteredWorkApplications = this.workApplications.filter(
        (app) =>
          app.id.toLowerCase().includes(search) ||
          app.contractingUnit.toLowerCase().includes(search) ||
          app.workLocation.toLowerCase().includes(search)
      );
    }
  }

  // 選擇施工申請單
  selectWorkApplication(application: WorkApplication): void {
    // 如果選中了同一個申請單，不做任何操作
    if (this.selectedWorkApplication && this.selectedWorkApplication.id === application.id) return;

    this.selectedWorkApplication = application;
    // 清空當前的工作日列表，表示正在加載
    this.workDaysList = [];

    // 載入該申請單的施工日程
    this.loadWorkDays(application.id);
    this.generateAvailableDates();
  }

  // 載入該申請單的施工日程
// 載入該申請單的施工日程
loadWorkDays(applicationId: string): void {
  // 根據不同的申請單ID返回不同的數據
  switch (applicationId) {
    case 'WORK-2023-001':
      this.workDaysList = [
        {
          id: 'WD001',
          date: '2025-06-01',
          isWorkingDay: true,
          workType1: true,
          workType2: false,
          workType3: false,
          workType4: false,
          workType5: true,
          workType6: false,
          workDescription: '更換A區機房配電盤',
          personnel: [
            {
              id: 'P001',
              name: '張志明',
              idNumber: 'A123456789',
              phone: '0912-345-678',
              jobType: '電工',
            },
            {
              id: 'P004',
              name: '李正雄',
              idNumber: 'D456789012',
              phone: '0945-678-901',
              jobType: '電工',
            },
          ],
        },
        {
          id: 'WD002',
          date: '2025-05-02',
          isWorkingDay: false,
          workType1: false,
          workType2: false,
          workType3: false,
          workType4: false,
          workType5: false,
          workType6: false,
          workDescription: '',
          personnel: [],
        },
        {
          id: 'WD003',
          date: '2025-05-03',
          isWorkingDay: true,
          workType1: false,
          workType2: false,
          workType3: true,
          workType4: true,
          workType5: false,
          workType6: false,
          workDescription: '電力系統維護',
          personnel: [
            {
              id: 'P001',
              name: '張志明',
              idNumber: 'A123456789',
              phone: '0912-345-678',
              jobType: '電工',
            },
          ],
        },
      ];
      break;

    case 'WORK-2023-002':
      this.workDaysList = [
        {
          id: 'WD101',
          date: '2025-05-10',
          isWorkingDay: true,
          workType1: false,
          workType2: false,
          workType3: false,
          workType4: false,
          workType5: false,
          workType6: true,
          workDescription: '冷氣空調系統安裝',
          personnel: [
            {
              id: 'P003',
              name: '王大明',
              idNumber: 'C345678901',
              phone: '0934-567-890',
              jobType: '空調技師',
            }
          ],
        },
        {
          id: 'WD102',
          date: '2025-05-11',
          isWorkingDay: true,
          workType1: false,
          workType2: true,
          workType3: false,
          workType4: false,
          workType5: false,
          workType6: false,
          workDescription: '空調室外機吊掛安裝',
          personnel: [
            {
              id: 'P003',
              name: '王大明',
              idNumber: 'C345678901',
              phone: '0934-567-890',
              jobType: '空調技師',
            },
            {
              id: 'P005',
              name: '陳美玲',
              idNumber: 'E567890123',
              phone: '0956-789-012',
              jobType: '機電工程師',
            }
          ],
        }
      ];
      break;

    case 'WORK-2023-003':
      this.workDaysList = [
        {
          id: 'WD201',
          date: '2025-05-05',
          isWorkingDay: true,
          workType1: false,
          workType2: false,
          workType3: true, // 局限空間作業
          workType4: true, // 有機溶劑作業
          workType5: false,
          workType6: false,
          workDescription: '餐廳水管維修及清潔',
          personnel: [
            {
              id: 'P002',
              name: '林小華',
              idNumber: 'B234567890',
              phone: '0923-456-789',
              jobType: '水電工',
              qualifications: ['局限空間作業證照', '有機溶劑作業證照']
            }
          ]
        },
        {
          id: 'WD202',
          date: '2025-05-06',
          isWorkingDay: false,
          workType1: false,
          workType2: false,
          workType3: false,
          workType4: false,
          workType5: false,
          workType6: false,
          workDescription: '',
          personnel: [],
        },
        {
          id: 'WD203',
          date: '2025-05-07',
          isWorkingDay: true,
          workType1: true,
          workType2: false,
          workType3: false,
          workType4: false,
          workType5: false,
          workType6: false,
          workDescription: '排水系統維護',
          personnel: [
            {
              id: 'P002',
              name: '林小華',
              idNumber: 'B234567890',
              phone: '0923-456-789',
              jobType: '水電工',
            },
            {
              id: 'P005',
              name: '陳美玲',
              idNumber: 'E567890123',
              phone: '0956-789-012',
              jobType: '機電工程師',
            }
          ],
        }
      ];
      break;

    default:
      // 如果沒有找到對應的申請單，就返回空列表
      this.workDaysList = [];
      break;
  }
}
  // 產生可選的日期（施工期間內）
  generateAvailableDates(): void {
    if (!this.selectedWorkApplication) return;

    const start = new Date(this.selectedWorkApplication.workPeriodStart);
    const end = new Date(this.selectedWorkApplication.workPeriodEnd);
    const dates: string[] = [];

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(this.formatDateValue(new Date(d)));
    }

    this.availableDates = dates;
  }

  // 打開新增/編輯工作日模態框
  openWorkDayModal(workDay?: WorkDay): void {
    this.editingWorkDay = workDay || null;

    if (workDay) {
      this.personnel = [...workDay.personnel];
      this.workDayForm.patchValue({
        workDate: workDay.date,
        isWorkingDay: workDay.isWorkingDay,
        workType1: workDay.workType1,
        workType2: workDay.workType2,
        workType3: workDay.workType3,
        workType4: workDay.workType4,
        workType5: workDay.workType5,
        workType6: workDay.workType6,
        workDescription: workDay.workDescription,
      });
    } else {
      this.personnel = [];
      this.workDayForm.reset({
        workDate: '',
        isWorkingDay: true,
        workType1: false,
        workType2: false,
        workType3: false,
        workType4: false,
        workType5: false,
        workType6: false,
        workDescription: '',
      });
    }

    this.showWorkDayModal = true;
  }

  // 關閉工作日模態框
  closeWorkDayModal(): void {
    this.showWorkDayModal = false;
    this.editingWorkDay = null;
  }

  // 儲存工作日
  saveWorkDay(): void {
    if (this.workDayForm.invalid) return;

    const formData = this.workDayForm.value;

    const workDay: WorkDay = {
      id: this.editingWorkDay?.id || `WD${Date.now()}`,
      date: formData.workDate,
      isWorkingDay: formData.isWorkingDay,
      workType1: formData.workType1,
      workType2: formData.workType2,
      workType3: formData.workType3,
      workType4: formData.workType4,
      workType5: formData.workType5,
      workType6: formData.workType6,
      workDescription: formData.workDescription,
      personnel: [...this.personnel],
    };

    // 如果是編輯現有工作日
    if (this.editingWorkDay) {
      const index = this.workDaysList.findIndex(
        (d) => d.id === this.editingWorkDay!.id
      );
      if (index !== -1) {
        this.workDaysList[index] = workDay;
      }
    } else {
      // 新增工作日
      this.workDaysList.push(workDay);
    }

    // 按日期排序
    this.workDaysList.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    this.closeWorkDayModal();
  }

  // 刪除工作日
  deleteWorkDay(workDay: WorkDay): void {
    if (confirm('確定要刪除此施工日程嗎？')) {
      const index = this.workDaysList.findIndex((d) => d.id === workDay.id);
      if (index !== -1) {
        this.workDaysList.splice(index, 1);
      }
    }
  }

  // 打開人員選擇模態框
  addPersonnel(): void {
    this.filteredContractorPersonnel = [...this.contractorPersonnel];
    this.selectedPersonnel = [...this.personnel];
    this.showPersonnelModal = true;
    this.personnelSearch = '';
  }

  // 關閉人員選擇模態框
  closePersonnelModal(): void {
    this.showPersonnelModal = false;
  }

  // 確認人員選擇
  confirmPersonnelSelection(): void {
    this.personnel = [...this.selectedPersonnel];
    this.closePersonnelModal();
  }

  // 移除人員
  removePersonnel(index: number): void {
    this.personnel.splice(index, 1);
  }

  // 過濾人員列表
  filterPersonnel(): void {
    const search = this.personnelSearch.toLowerCase();
    this.filteredContractorPersonnel = this.contractorPersonnel.filter(
      (p) =>
        p.name.toLowerCase().includes(search) ||
        p.idNumber.toLowerCase().includes(search) ||
        p.jobType.toLowerCase().includes(search)
    );
  }

  // 檢查人員是否被選中
  isPersonnelSelected(person: Personnel): boolean {
    return this.selectedPersonnel.some((p) => p.id === person.id);
  }

  // 切換人員選擇狀態
  togglePersonnelSelection(person: Personnel): void {
    const index = this.selectedPersonnel.findIndex((p) => p.id === person.id);
    if (index === -1) {
      this.selectedPersonnel.push(person);
    } else {
      this.selectedPersonnel.splice(index, 1);
    }
  }

  // 判斷工作日是否有特殊作業
  hasSpecialWork(workDay: WorkDay): boolean {
    return (
      workDay.workType1 ||
      workDay.workType2 ||
      workDay.workType3 ||
      workDay.workType4 ||
      workDay.workType5 ||
      workDay.workType6
    );
  }

  // 格式化日期顯示（2023-05-01 -> 2023年05月01日）
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}年${month}月${day}日`;
  }

  // 格式化日期值（Date -> yyyy-MM-dd）
  formatDateValue(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // 獲取狀態文字
  getStatusText(status: string): string {
    switch (status) {
      case 'pending':
        return '審核中';
      case 'approved':
        return '已核准';
      case 'rejected':
        return '已拒絕';
      case 'completed':
        return '已完成';
      default:
        return '未知狀態';
    }
  }

  // 獲取資格確認狀態CSS類別
  getQualificationStatusClass(workDay: WorkDay): string {
    if (!workDay.isWorkingDay || !workDay.personnel.length) return '';

    const status = this.checkQualifications(workDay);
    if (status === 'qualified') return 'qualification-success';
    if (status === 'partial') return 'qualification-warning';
    return 'qualification-danger';
  }

  // 獲取資格確認狀態圖標
  getQualificationStatusIcon(workDay: WorkDay): string {
    const status = this.checkQualifications(workDay);
    if (status === 'qualified') return 'fas fa-check-circle';
    if (status === 'partial') return 'fas fa-exclamation-circle';
    return 'fas fa-times-circle';
  }

  // 獲取資格確認狀態文字
  getQualificationStatusText(workDay: WorkDay): string {
    const status = this.checkQualifications(workDay);
    if (status === 'qualified') return '資格完整';
    if (status === 'partial') return '部分符合';
    return '資格不符';
  }

  // 檢查是否有資格問題
  hasQualificationIssues(workDay: WorkDay): boolean {
    return this.checkQualifications(workDay) !== 'qualified';
  }

  // 獲取資格問題列表
  getQualificationIssues(workDay: WorkDay): string[] {
    // 這裡模擬獲取資格問題
    const issues: string[] = [];

    // 檢查每一種特殊作業所需的證照
    if (workDay.workType1) { // 動火作業
      const hasFireWorkCertified = workDay.personnel.some(p =>
        p.qualifications?.includes('動火作業證照')
      );
      if (!hasFireWorkCertified) {
        issues.push('缺少動火作業證照人員');
      }
    }

    if (workDay.workType2) { // 吊掛作業
      const hasLiftingWorkCertified = workDay.personnel.some(p =>
        p.qualifications?.includes('吊掛作業證照')
      );
      if (!hasLiftingWorkCertified) {
        issues.push('缺少吊掛作業證照人員');
      }
    }

    if (workDay.workType3) { // 局限空間作業
      const hasConfinedSpaceCertified = workDay.personnel.some(p =>
        p.qualifications?.includes('局限空間作業證照')
      );
      if (!hasConfinedSpaceCertified) {
        issues.push('缺少局限空間作業證照人員');
      }
    }

    if (workDay.workType4) { // 有機溶劑作業
      const hasOrganicSolventCertified = workDay.personnel.some(p =>
        p.qualifications?.includes('有機溶劑作業證照')
      );
      if (!hasOrganicSolventCertified) {
        issues.push('缺少有機溶劑作業證照人員');
      }
    }

    if (workDay.workType5) { // 高架作業
      const hasHeightWorkCertified = workDay.personnel.some(p =>
        p.qualifications?.includes('高架作業證照')
      );
      if (!hasHeightWorkCertified) {
        issues.push('缺少高架作業證照人員');
      }
    }

    if (workDay.workType6) { // 消防系統作業
      const hasFireSystemCertified = workDay.personnel.some(p =>
        p.qualifications?.includes('消防系統作業證照')
      );
      if (!hasFireSystemCertified) {
        issues.push('缺少消防系統作業證照人員');
      }
    }

    return issues;
  }

  // 檢查資格狀態 (qualified: 完全符合, partial: 部分符合, unqualified: 不符合)
  private checkQualifications(workDay: WorkDay): 'qualified' | 'partial' | 'unqualified' {
    // 如果不是工作日或沒有人員，則返回合格
    if (!workDay.isWorkingDay || !workDay.personnel.length) return 'qualified';

    // 模擬檢查資格的邏輯
    const issues = this.getQualificationIssues(workDay);

    // 如果特殊作業都沒有選擇，則返回合格
    if (!this.hasSpecialWork(workDay)) return 'qualified';

    // 依據問題數量和特殊作業類型總數來判斷
    const specialWorkCount = this.getSpecialWorkCount(workDay);

    if (issues.length === 0) return 'qualified';
    if (issues.length < specialWorkCount) return 'partial';
    return 'unqualified';
  }

  // 計算特殊作業類型數量
  private getSpecialWorkCount(workDay: WorkDay): number {
    let count = 0;
    if (workDay.workType1) count++;
    if (workDay.workType2) count++;
    if (workDay.workType3) count++;
    if (workDay.workType4) count++;
    if (workDay.workType5) count++;
    if (workDay.workType6) count++;
    return count;
  }
}
